import VanillaFlicking__default, { EVENTS, withFlickingMethods, ExternalRenderer, getFlickingAttached, getDefaultCameraTransform, VirtualRenderingStrategy, NormalRenderingStrategy, sync, range, CLASS } from '@egjs/flicking';
const _c0 = ["*", [["", "in-viewport", ""]]];
const _c1 = ["*", "[in-viewport]"];
export * from '@egjs/flicking';
import * as i0 from '@angular/core';
import { Directive, EventEmitter, PLATFORM_ID, Component, ViewEncapsulation, Inject, Input, Output, HostBinding, ContentChildren, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import ListDiffer from '@egjs/list-differ';
import { __decorate } from 'tslib';

/*
 * Copyright (c) 2015 NAVER Corp.
 * egjs projects are licensed under the MIT license
 */
let NgxFlickingPanel = /*#__PURE__*/(() => {
  class NgxFlickingPanel {
    get nativeElement() {
      return this._host.nativeElement;
    }
    get rendered() {
      return this._rendered;
    }
    constructor(_host, _renderer) {
      this._host = _host;
      this._renderer = _renderer;
      this._rendered = true;
    }
    show(flicking) {
      this._rendered = true;
      const el = this.nativeElement;
      const cameraEl = flicking.camera.element;
      if (el.parentElement !== cameraEl) {
        this._renderer.appendChild(cameraEl, el);
      }
    }
    hide(flicking) {
      this._rendered = false;
      const el = this.nativeElement;
      const cameraEl = flicking.camera.element;
      if (el.parentElement === cameraEl) {
        this._renderer.removeChild(cameraEl, el);
      }
    }
  }
  NgxFlickingPanel.ɵfac = function NgxFlickingPanel_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NgxFlickingPanel)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
  };
  NgxFlickingPanel.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: NgxFlickingPanel,
    selectors: [["", "flicking-panel", ""], ["", "FlickingPanel", ""]],
    standalone: true
  });
  return NgxFlickingPanel;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const EVENT_NAMES = Object.keys(EVENTS).map(key => EVENTS[key]);
let FlickingInterface = /*#__PURE__*/(() => {
  class FlickingInterface {}
  __decorate([withFlickingMethods], FlickingInterface.prototype, "_vanillaFlicking", void 0);
  return FlickingInterface;
})();
class NgxRenderer extends ExternalRenderer {
  constructor(options) {
    super(options);
    this._ngxFlicking = options.ngxFlicking;
    this._ngxRenderer = options.ngxRenderer;
  }
  // eslint-disable-next-line @typescript-eslint/require-await
  async render() {
    const flicking = getFlickingAttached(this._flicking);
    const strategy = this._strategy;
    strategy.updateRenderingPanels(flicking);
    strategy.renderPanels(flicking);
    this._resetPanelElementOrder();
    this._afterRender();
  }
  _collectPanels() {
    const flicking = getFlickingAttached(this._flicking);
    const children = this._ngxFlicking.ngxPanels.toArray();
    this._panels = this._strategy.collectPanels(flicking, children);
  }
  _createPanel(externalComponent, options) {
    return this._strategy.createPanel(externalComponent, options);
  }
  _resetPanelElementOrder() {
    const flicking = getFlickingAttached(this._flicking);
    const renderer = this._ngxRenderer;
    const cameraEl = flicking.camera.element;
    // We're using reversed panels here as last panel should be the last element of camera element
    const reversedElements = this._strategy.getRenderingElementsByOrder(flicking).reverse();
    reversedElements.forEach((el, idx) => {
      const nextEl = reversedElements[idx - 1] ? reversedElements[idx - 1] : null;
      if (el.nextElementSibling !== nextEl) {
        renderer.insertBefore(cameraEl, el, nextEl);
      }
    });
  }
}
class NgxElementProvider {
  get element() {
    return this._el.nativeElement;
  }
  get rendered() {
    return this._el.rendered;
  }
  constructor(el) {
    this._el = el;
  }
  show(flicking) {
    this._el.show(flicking);
  }
  hide(flicking) {
    this._el.hide(flicking);
  }
}

/**
 * Copyright (c) 2015 NAVER Corp.
 * egjs projects are licensed under the MIT license
 */
let NgxFlickingComponent = /*#__PURE__*/(() => {
  class NgxFlickingComponent extends FlickingInterface {
    get isVertical() {
      return this.options.horizontal === false;
    }
    get isHiddenBeforeInit() {
      const initialized = this._vanillaFlicking && this._vanillaFlicking.initialized;
      return this.hideBeforeInit && !initialized;
    }
    get cameraStyleBeforeInit() {
      const initialized = this._vanillaFlicking && this._vanillaFlicking.initialized;
      return !initialized && this.firstPanelSize ? {
        transform: getDefaultCameraTransform(this.options.align, this.options.horizontal, this.firstPanelSize)
      } : {};
    }
    get ngxPanels() {
      return this._ngxPanels;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    get _cameraElClass() {
      return `flicking-camera ${this.cameraClass ?? ""}`.trim();
    }
    constructor(_elRef, _ngxRenderer, _platformId, _ngZone) {
      super();
      this._elRef = _elRef;
      this._ngxRenderer = _ngxRenderer;
      this._platformId = _platformId;
      this._ngZone = _ngZone;
      this.options = {};
      this.plugins = [];
      this.hideBeforeInit = false;
      this._pluginsDiffer = new ListDiffer();
      this._destroy$ = new Subject();
      this._vanillaFlicking = null;
      EVENT_NAMES.forEach(evtName => {
        this[evtName] = new EventEmitter();
      });
    }
    ngAfterViewInit() {
      if (!isPlatformBrowser(this._platformId)) return;
      const options = this.options;
      const viewportEl = this._elRef.nativeElement;
      const virtual = options.virtual && options.panelsPerView > 0;
      const rendererOptions = {
        ngxFlicking: this,
        align: options.align,
        ngxRenderer: this._ngxRenderer,
        strategy: virtual ? new VirtualRenderingStrategy() : new NormalRenderingStrategy({
          providerCtor: NgxElementProvider
        })
      };
      if (virtual) {
        this._initVirtualElements();
      }
      // Initialize `VanillaFlicking` outside of the Angular zone so it will set up `mousedown`
      // and `mousemove` event listeners within the root zone, and it won't trigger change detection
      // every time the user moves the mouse.
      const flicking = this._ngZone.runOutsideAngular(() => new VanillaFlicking__default(viewportEl, {
        ...this.options,
        externalRenderer: new NgxRenderer(rendererOptions)
      }));
      this._vanillaFlicking = flicking;
      const elementDiffer = new ListDiffer(this._ngxPanels.toArray());
      this._bindEvents();
      this._checkPlugins();
      if (this.status) {
        flicking.setStatus(this.status);
      }
      // Note: doesn't need to unsubscribe, because `changes`
      // gets completed by Angular when the view is destroyed.
      this._ngxPanels.changes.subscribe(() => {
        const panels = this._ngxPanels.toArray();
        const diffResult = elementDiffer.update(panels);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        sync(flicking, diffResult, [...diffResult.maintained.map(([_, idx]) => diffResult.list[idx]), ...diffResult.added.map(idx => diffResult.list[idx])]);
      });
    }
    ngOnDestroy() {
      this._destroy$.next();
      this._vanillaFlicking?.destroy();
    }
    ngOnChanges() {
      const flicking = this._vanillaFlicking;
      if (!flicking) return;
      this._ngZone.runOutsideAngular(() => {
        void flicking.renderer.forceRenderAllPanels();
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        virtual,
        ...newOptions
      } = this.options;
      // Omit 'virtual', as it can't have any setter
      for (const key in newOptions) {
        if (key in flicking && flicking[key] !== newOptions[key]) {
          flicking[key] = newOptions[key];
        }
      }
      this._checkPlugins();
    }
    _bindEvents() {
      const flicking = this._vanillaFlicking;
      EVENT_NAMES.forEach(evtName => {
        // `fromEvent` supports passing `JQueryStyleEventEmitter`
        // (an object with `on` and `off` methods). `flicking` acts as a
        // jQuery event emitter since it has both methods.
        fromEvent(flicking, evtName).pipe(takeUntil(this._destroy$)).subscribe(e => {
          // Style guide: Event - https://angular.io/guide/styleguide#dont-prefix-output-properties
          const emitter = this[evtName];
          e.currentTarget = this;
          if (emitter && emitter.observers.length > 0) {
            this._ngZone.run(() => emitter.emit(e));
          }
        });
      });
    }
    _checkPlugins() {
      const flicking = this._vanillaFlicking;
      if (!flicking) return;
      const {
        list,
        added,
        removed,
        prevList
      } = this._pluginsDiffer.update(this.plugins);
      flicking.addPlugins(...added.map(index => list[index]));
      flicking.removePlugins(...removed.map(index => prevList[index]));
    }
    _initVirtualElements() {
      const options = this.options;
      const renderer = this._ngxRenderer;
      const cameraElement = this._elRef.nativeElement.firstElementChild;
      const panelsPerView = options.panelsPerView;
      const virtual = options.virtual;
      const fragment = document.createDocumentFragment();
      const newElements = range(panelsPerView + 1).map(idx => {
        const panelEl = renderer.createElement("div");
        panelEl.className = virtual.panelClass ?? CLASS.DEFAULT_VIRTUAL;
        panelEl.dataset.elementIndex = idx.toString();
        return panelEl;
      });
      newElements.forEach(el => {
        fragment.appendChild(el);
      });
      renderer.appendChild(cameraElement, fragment);
    }
  }
  NgxFlickingComponent.ɵfac = function NgxFlickingComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NgxFlickingComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.NgZone));
  };
  NgxFlickingComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: NgxFlickingComponent,
    selectors: [["ngx-flicking"], ["", "NgxFlicking", ""]],
    contentQueries: function NgxFlickingComponent_ContentQueries(rf, ctx, dirIndex) {
      if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, NgxFlickingPanel, 4);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._ngxPanels = _t);
      }
    },
    hostAttrs: [1, "flicking-viewport", 2, "display", "block"],
    hostVars: 4,
    hostBindings: function NgxFlickingComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("vertical", ctx.isVertical)("flicking-hidden", ctx.isHiddenBeforeInit);
      }
    },
    inputs: {
      options: "options",
      plugins: "plugins",
      status: "status",
      cameraClass: "cameraClass",
      hideBeforeInit: "hideBeforeInit",
      firstPanelSize: "firstPanelSize"
    },
    outputs: {
      ready: "ready",
      beforeResize: "beforeResize",
      afterResize: "afterResize",
      holdStart: "holdStart",
      holdEnd: "holdEnd",
      moveStart: "moveStart",
      move: "move",
      moveEnd: "moveEnd",
      willChange: "willChange",
      changed: "changed",
      willRestore: "willRestore",
      restored: "restored",
      select: "select",
      needPanel: "needPanel",
      visibleChange: "visibleChange",
      reachEdge: "reachEdge",
      panelChange: "panelChange"
    },
    standalone: true,
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
    ngContentSelectors: _c1,
    decls: 3,
    vars: 2,
    consts: [[3, "ngClass", "ngStyle"]],
    template: function NgxFlickingComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef(_c0);
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵprojection(1);
        i0.ɵɵelementEnd();
        i0.ɵɵprojection(2, 1);
      }
      if (rf & 2) {
        i0.ɵɵproperty("ngClass", ctx._cameraElClass)("ngStyle", ctx.cameraStyleBeforeInit);
      }
    },
    dependencies: [CommonModule, i1.NgClass, i1.NgStyle],
    styles: [".flicking-viewport{position:relative;overflow:hidden}.flicking-viewport.vertical{display:-webkit-inline-box;display:-ms-inline-flexbox;display:inline-flex}.flicking-viewport.vertical>.flicking-camera{display:-webkit-inline-box;display:-ms-inline-flexbox;display:inline-flex;-webkit-box-orient:vertical;-webkit-box-direction:normal;-ms-flex-direction:column;flex-direction:column}.flicking-viewport.flicking-hidden>.flicking-camera>*{visibility:hidden}.flicking-camera{width:100%;height:100%;display:-webkit-box;display:-ms-flexbox;display:flex;position:relative;-webkit-box-orient:horizontal;-webkit-box-direction:normal;-ms-flex-direction:row;flex-direction:row;z-index:1;will-change:transform}.flicking-camera>*{-ms-flex-negative:0;flex-shrink:0}\n"],
    encapsulation: 2
  });
  return NgxFlickingComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxFlickingModule = /*#__PURE__*/(() => {
  class NgxFlickingModule {}
  NgxFlickingModule.ɵfac = function NgxFlickingModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NgxFlickingModule)();
  };
  NgxFlickingModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: NgxFlickingModule
  });
  NgxFlickingModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    imports: [NgxFlickingComponent]
  });
  return NgxFlickingModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-flicking
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxFlickingComponent, NgxFlickingModule, NgxFlickingPanel };
